import {
  IonItem,
  IonLabel,
  IonLoading,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { getUserSetups } from "../api/UserSetups";
import { TranslationsContext } from "../util/Translations";
import {
  saveEamSetting,
  useSaveErrorToast,
} from "../pages/settings/settingsShared";

export const ROWS_TO_SHOW_SECTION = "QuerySettings";
export const ROWS_TO_SHOW_PARAMETER = "rows_to_show";
const ROW_OPTIONS = [50, 100, 200, 500, 1000];

/** Reads the saved rows-per-query value (undefined when not set). */
export const readRowsToShow = async () => {
  const response = await getUserSetups({
    userSection: ROWS_TO_SHOW_SECTION,
    userParameter: ROWS_TO_SHOW_PARAMETER,
  });
  const saved = Number(response?.data?.UserValue);
  return response?.isError || !saved ? undefined : saved;
};

interface RowSelectorProps {
  disabled?: boolean;
}

const ShowQueryDataResult: React.FC<RowSelectorProps> = ({ disabled }) => {
  const { translations } = useContext(TranslationsContext);
  const showSaveError = useSaveErrorToast();
  const [rowsToShow, setRowsToShow] = useState<number | undefined>();
  const [showLoading, setShowLoading] = useState(false);
  const savedRows = useRef<number | undefined>();

  useEffect(() => {
    readRowsToShow().then((value) => {
      savedRows.current = value;
      setRowsToShow(value);
    });
  }, []);

  const handleSelectionChange = async (value: number) => {
    if (!value || value === savedRows.current) return;
    setRowsToShow(value);
    setShowLoading(true);
    const saved = await saveEamSetting(
      ROWS_TO_SHOW_SECTION,
      ROWS_TO_SHOW_PARAMETER,
      value
    );
    setShowLoading(false);
    if (!saved) {
      setRowsToShow(savedRows.current);
      showSaveError();
      return;
    }
    savedRows.current = value;
  };

  // keep a saved value visible even if it is not one of the standard options
  const options =
    rowsToShow && !ROW_OPTIONS.includes(rowsToShow)
      ? [...ROW_OPTIONS, rowsToShow].sort((a, b) => a - b)
      : ROW_OPTIONS;

  return (
    <IonItem>
      <IonLoading isOpen={showLoading} />
      <IonLabel position="stacked">
        {translations["lbl_mob_rows_per_query"] || "Rows per query result"}
      </IonLabel>
      <IonSelect
        interface="popover"
        value={rowsToShow}
        disabled={disabled}
        placeholder={translations["lbl_btn_select"] || "Select"}
        onIonChange={(event) => handleSelectionChange(event.detail.value)}
      >
        {options.map((option) => (
          <IonSelectOption key={option} value={option}>
            {`${option} ${translations["lbl_mob_rows"] || "rows"}`}
          </IonSelectOption>
        ))}
      </IonSelect>
    </IonItem>
  );
};

export default ShowQueryDataResult;
